import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { isNativePlatform } from "@/utils/native";

export function base64ToBlob(base64: string, mimeType: string): Blob {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

// Chunked because spreading a multi-megabyte array into one fromCharCode call
// overflows the call stack.
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += chunkSize) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
  }
  return btoa(chunks.join(""));
}

function triggerAnchorDownload(url: string, fileName: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

/**
 * Saves a binary file on the current platform. On web it triggers a regular
 * browser download; in the mobile app — where anchor downloads do nothing
 * inside the WebView — it writes the file to the app cache and opens the
 * system share sheet (save to Files, AirDrop, print, …).
 *
 * Resolves false when the share sheet was dismissed; that is not an error.
 */
export async function saveBase64File({
  base64,
  fileName,
  mimeType,
}: {
  base64: string;
  fileName: string;
  mimeType: string;
}): Promise<boolean> {
  if (isNativePlatform()) {
    const { uri } = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
    });

    try {
      await Share.share({ title: fileName, files: [uri] });
    } catch (error) {
      if (error instanceof Error && /cancel/i.test(error.message)) {
        return false;
      }
      throw error;
    }
    return true;
  }

  const url = URL.createObjectURL(base64ToBlob(base64, mimeType));

  triggerAnchorDownload(url, fileName);
  URL.revokeObjectURL(url);
  return true;
}

/**
 * Opens a base64 file for viewing in a new tab. The bytes must never ride in the
 * URL — a `data:` href resolved against the origin becomes a request path long
 * enough for the server to reject with 414. The mobile app has no inline viewer
 * for a new tab, so there it falls back to the share sheet.
 */
export async function openBase64File({
  base64,
  fileName,
  mimeType,
}: {
  base64: string;
  fileName: string;
  mimeType: string;
}): Promise<void> {
  if (isNativePlatform()) {
    await saveBase64File({ base64, fileName, mimeType });
    return;
  }

  const url = URL.createObjectURL(base64ToBlob(base64, mimeType));

  // No `noopener`/`noreferrer` feature string — either makes window.open return
  // null, indistinguishable from a blocked popup. Sever the opener below instead.
  const opened = window.open(url, "_blank");

  if (opened) {
    opened.opener = null;
  } else {
    triggerAnchorDownload(url, fileName);
  }

  // Revoke late so the new tab has time to load the Blob before it is freed.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
